import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { AnimatePresence } from 'framer-motion';
import { Toaster } from 'react-hot-toast';

// Components
import Header from './components/Header';
import LoadingScreen from './components/LoadingScreen';
import WorkflowManager from './components/WorkflowManager';
import StoryStage from './components/stages/StoryStage';
import DialoguesStage from './components/stages/DialoguesStage';
import QuestionsStage from './components/stages/QuestionsStage';
import CompletedStage from './components/stages/CompletedStage';

// Context
import { WorkflowProvider, useWorkflow } from './context/WorkflowContext';

const AppContainer = styled.div`
  width: 100vw;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.3) 0%, transparent 50%);
  z-index: 0;
`;

const MainContent = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 80px;
  
  @media (max-width: 768px) {
    padding-top: 70px;
  }
  
  @media (max-width: 480px) {
    padding-top: 100px;
  }
`;

// Component that uses workflow context
function AppContent() {
  const { currentStage, status, completedStages } = useWorkflow();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AppContainer>
      <BackgroundPattern />
      <MainContent>
        <Header 
          currentStage={currentStage}
          status={status}
          completedStages={completedStages}
        />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<WorkflowManager />} />
            <Route path="/story" element={<StoryStage />} />
            <Route path="/dialogues" element={<DialoguesStage />} />
            <Route path="/questions" element={<QuestionsStage />} />
            <Route path="/completed" element={<CompletedStage />} />
          </Routes>
        </AnimatePresence>
      </MainContent>
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '8px',
          },
        }}
      />
    </AppContainer>
  );
}

function App() {
  return (
    <WorkflowProvider>
      <AppContent />
    </WorkflowProvider>
  );
}

export default App;


